import React from "react";

/**
 * Reusable, framework-agnostic skeleton table for Tailwind projects.
 *
 * Props:
 * - rows: number of skeleton rows to render (default 8)
 * - cols: array describing each column
 *     - "checkbox" renders a square (for select boxes)
 *     - number (e.g. 120) sets a fixed width in px for a line bar
 *     - string (e.g. "w-24" or "160px") sets width (Tailwind class or raw px)
 *     - object: { type?: 'checkbox'|'line', width?: number|string, height?: number, className?: string }
 * - showHeader: whether to render a simple header row (default true)
 * - headerLabels: optional array of header labels (strings or JSX)
 * - className: extra classes for the outer wrapper
 * - tableClassName: extra classes for <table>
 * - rowClassName: extra classes for each <tr>
 * - cellClassName: extra classes for each <td>
 * - pulse: true (default) uses Tailwind `animate-pulse`
 * - shimmer: false (optional) if true, uses gradient shimmer
 */
const SkeletonTable = ({
  rows = 8,
  cols = [],
  showHeader = true,
  headerLabels = [],
  className = "",
  tableClassName = "",
  rowClassName = "",
  cellClassName = "",
  pulse = true,
  shimmer = false,
}) => {
  const effectClass = shimmer
    ? "bg-[length:200%_100%] bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-[shimmer_1.6s_infinite]"
    : "bg-gray-200 animate-pulse";

  // normalize a column descriptor into a render plan
  const normCol = (c) => {
    if (typeof c === "number") return { type: "line", width: `${c}px` };
    if (typeof c === "string") {
      if (c === "checkbox") return { type: "checkbox" };
      // treat string as width (tailwind class or raw css)
      const isTailwindClass = c.startsWith("w-") || c.startsWith("max-w-");
      return { type: "line", width: isTailwindClass ? c : `${c}` };
    }
    if (typeof c === "object" && c) return { type: "line", ...c };
    return { type: "line", width: "6rem" };
  };

  const colsPlan = cols.map(normCol);

  return (
    <div className={`overflow-x-auto ${className}`}>
      {/* optional keyframes for shimmer, safely ignored if not used */}
      {shimmer && (
        <style>{`@keyframes shimmer { 100% { background-position: -200% 0; } }`}</style>
      )}
      <table className={`w-full text-sm border-collapse ${tableClassName}`}>
        {showHeader && (
          <thead>
            <tr className="bg-gray-50 text-gray-600 uppercase text-xs">
              {headerLabels.length > 0
                ? headerLabels.map((h, idx) => (
                    <th key={idx} className="px-4 py-3 font-semibold text-left">
                      {h}
                    </th>
                  ))
                : colsPlan.map((_, idx) => (
                    <th key={idx} className="px-4 py-3 font-semibold text-left">
                      <div className={`h-3 w-20 rounded ${effectClass}`} />
                    </th>
                  ))}
            </tr>
          </thead>
        )}

        <tbody>
          {Array.from({ length: rows }).map((_, rIdx) => (
            <tr key={rIdx} className={`border-t ${rowClassName}`}>
              {colsPlan.map((c, cIdx) => {
                const base = `rounded ${effectClass}`;
                const height =
                  typeof c.height === "number" ? `${c.height}px` : "0.75rem";

                // width handling: tailwind class vs inline style
                const widthClass =
                  typeof c.width === "string" && c.width.startsWith("w-")
                    ? c.width
                    : null;
                const styleWidth =
                  typeof c.width === "string" && !c.width.startsWith("w-")
                    ? { width: c.width }
                    : typeof c.width === "number"
                    ? { width: `${c.width}px` }
                    : undefined;

                return (
                  <td key={cIdx} className={`px-4 py-3 ${cellClassName}`}>
                    {c.type === "checkbox" ? (
                      <div className={`h-4 w-4 ${base} ${c.className || ""}`} />
                    ) : (
                      <div
                        className={`${base} ${widthClass || "w-24"}`}
                        style={{ height, ...(styleWidth || {}) }}
                      />
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SkeletonTable;
